import Flex from "@/components/Flex/Flex";
import Checkbox from "@/components/forms/Checkbox/Checkbox";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import { useEffect, useState } from "react";
import { useShipmentContext } from "@/contexts/ShipmentContext";
import IconInfo from "@/components/icons/IconInfo";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import Button from "@/components/forms/Button/Button";

import styles from "./CloneShipmentModal.module.css";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { FbaBatchStatus } from "@boxem/core-api-schema";
import { useLocation } from "wouter";


export interface CloneShipmentModalProps {
  open: boolean;
  focusedShipmentId?: number;
  onClose: () => void;
}

export function CloneShipmentModal(props: CloneShipmentModalProps) {
  const { open, onClose, focusedShipmentId } = props;

  const {currentBatch, cloneShipment} = useShipmentContext();
  const {handleErrorResponseGenerically} = useErrorModalContext();

  const [cancelShipment, setCancelShipment] = useState<boolean>(true);
  const [cloneAllSKUs, setCloneAllSKUs] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [location, navigate] = useLocation();

  useEffect(() => {
    // ensure you don't get the option to cancel a batch that is already cancelled:
    if (currentBatch?.status === FbaBatchStatus.BATCH_CANCELLED) {
      setCancelShipment(false);
    }
  }, [currentBatch]);

  const hasMultipleShipments = currentBatch?.shipments?.length > 1;

  return (
    <Modal
      open={open}
      onOpenChange={o => { if (!o && !loading) onClose(); }}
    >
      <ModalHeader><h3>Clone Shipment</h3></ModalHeader>
      <ModalBody>

        <Flex direction="column" justify="center" align="center" gap={2} className={styles.body}>
          {loading && (
            <>
              <p>Working on it...</p>
              <LoadingSpinner/>
            </>
          )}
          {!loading && (
            <>
              <p>Cloning a shipment creates a new shipment with the same SKUs and quantities, and puts you back in Step 1. It's useful when you want to re-make this shipment but with minor changes.</p>
              
              <Flex gap={2}>
                {currentBatch?.status !== FbaBatchStatus.BATCH_CANCELLED && (
                  <label className={styles.label}>
                    <Tooltip title={hasMultipleShipments ? "If enabled, all of the shipments for your selected placement option will be cancelled (not just the one currently selected!)" : "If enabled, the current shipment will be cancelled"}>
                      <IconInfo/>
                    </Tooltip>
                    <span>Cancel current shipment?</span>
                    <Checkbox
                      value={cancelShipment}
                      onChange={v => setCancelShipment(v)}
                    />
                  </label>
                )}

                {hasMultipleShipments && (
                  <label className={styles.label}>
                    <Tooltip title={"If enabled, the cloned shipment will contain SKUs from all shipments in your selected placement option, not just the one currently selected"}>
                      <IconInfo/>
                    </Tooltip>
                    <span>Clone all SKUs</span>
                    <Checkbox
                      value={cloneAllSKUs}
                      onChange={v => setCloneAllSKUs(v)}
                    />
                  </label>
                )}
              </Flex>
            </>
          )}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Flex justify="space-between" gap={2}>
          <Button
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            type={cancelShipment ? "danger" : "primary"}
            disabled={loading}
            onClick={async () => {
              setLoading(true);
              try {
                const {id} = await cloneShipment(cancelShipment, cloneAllSKUs ? null : focusedShipmentId);
                navigate(`/fba/shipments/${id}`);
              } catch (e) {
                handleErrorResponseGenerically(e);
              } finally {
                setLoading(false);
              }
              onClose();
            }}
          >
            {cancelShipment ? "Cancel current shipment and create clone" : "Clone Shipment"}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
}